import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AlertController, LoadingController, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
// Rxjs
import { from, Observable, of } from 'rxjs';
import { delayWhen, filter, map, switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class OverlaysService {
  constructor(
    private alertController: AlertController,
    private loadingController: LoadingController,
    private toastController: ToastController,
    private translateService: TranslateService
  ) {}

  // Loader

  public presentLoader$(loadingMessage?) {
    return delayWhen(() => from(this.presentIonicLoader(loadingMessage)));
  }

  public dismissLoader$() {
    return delayWhen(() => from(this.dismissIonicLoader()));
  }

  public async presentIonicLoader(options?) {
    if (!(await this.loadingController.getTop())) {
      const loading = await this.loadingController.create(options);
      await loading.present();
    }
  }

  public async dismissIonicLoader() {
    if (await this.loadingController.getTop()) {
      await this.loadingController.dismiss();
    }
  }

  // Alert

  public presentConfirmAlert(message: string) {
    return Observable.create(async (observer) => {
      const alert = await this.alertController.create({
        message,
        buttons: [
          {
            text: this.translateService.instant('ACTIONS.DISCARD_CHANGES'),
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => observer.error('error'),
          },
          {
            text: this.translateService.instant('ACTIONS.CONFIRM'),
            handler: () => {
              observer.next(true);
              observer.complete();
            },
          },
        ],
      });
      alert.present();
    });
  }

  public async presentInfoAlert(message: string, header = '') {
    const alert = await this.alertController.create({
      header: this.translateService.instant(header || 'ALERTS.INFO'),
      message: this.translateService.instant(message),
      buttons: [
        {
          text: this.translateService.instant('ALERTS.ACCEPT_BUTTON'),
          handler: () => {},
        },
      ],
    });

    await alert.present();
  }

  // Toast

  public presentToast$(message) {
    return delayWhen(() => from(this.presentIonicToast(message, 'primary')));
  }

  public presentSuccessToast$(message) {
    return delayWhen(() => from(this.presentIonicToast(message, 'success')));
  }

  public presentErrorToast$(message) {
    return delayWhen(() => from(this.presentIonicToast(message, 'danger')));
  }

  public async presentIonicToast(message, color?) {
    const toast = await this.toastController.create({
      message: this.translateService.instant(message),
      color,
      duration: 5000,
    });
    await toast.present();
  }

  // Form

  public processForm$() {
    return switchMap((formGroup: FormGroup) =>
      of(formGroup).pipe(
        tap((form) => {
          if (form.valid) {
            return;
          }
          form.markAllAsTouched();
          //
          if (form.get('data')) {
            form.get('data').markAllAsTouched();
          }
          this.presentInfoAlert('ALERTS.INVALID_FORM');
        }),
        filter((form) => form.valid),
        map((form) => form.value)
      )
    );
  }

  public processFormWithPoliticsCheck$(fieldName) {
    return switchMap((formGroup: FormGroup) =>
      of(formGroup).pipe(
        tap((form) => {
          if (form.valid) {
            return;
          }
          form.markAllAsTouched();

          const controls = Object.keys(form.controls).filter((key) => key != fieldName);

          if (controls.every((controlName) => form.controls[controlName].valid) && form.get(fieldName).invalid) {
            this.presentInfoAlert('ALERTS.INVALID_POLITICS');
            return;
          }
          if (form.get('data')) {
            form.get('data').markAllAsTouched();
          }
          this.presentInfoAlert('ALERTS.INVALID_FORM');
        }),
        filter((form) => form.valid),
        map((form) => form.value)
      )
    );
  }

  public processLanguageErrors$(languagesToValidate) {
    return switchMap((formGroup: FormGroup) =>
      of(formGroup).pipe(
        map((form) => ({
          form,
          errorLangs: languagesToValidate.filter((lang) => !form.get('data').get(lang).valid),
        }))
      )
    );
  }
}
